import React, { useMemo } from 'react';
import { RemoteCursor } from '../../hooks/useCursorSync';

/**
 * Props for UserCursor component
 */
interface UserCursorProps {
  /**
   * Remote cursor data
   */
  cursor: RemoteCursor;

  /**
   * Stage position for coordinate conversion
   */
  stagePos: { x: number; y: number };

  /**
   * Stage scale for sizing
   */
  stageScale: number;

  /**
   * Viewport dimensions for visibility check
   */
  viewportWidth: number;
  viewportHeight: number;
}

/**
 * Props for UserCursors container component
 */
interface UserCursorsProps {
  /**
   * Array of remote cursors to render
   */
  cursors: RemoteCursor[];

  /**
   * Stage position
   */
  stagePos: { x: number; y: number };

  /**
   * Stage scale
   */
  stageScale: number;

  /**
   * Viewport dimensions
   */
  viewportWidth: number;
  viewportHeight: number;
}

/**
 * Single user cursor component
 */
const UserCursor: React.FC<UserCursorProps> = ({
  cursor,
  stagePos,
  stageScale,
  viewportWidth,
  viewportHeight,
}) => {
  // Convert canvas coordinates to screen coordinates
  const screenPos = useMemo(() => {
    return {
      x: cursor.cursor.x * stageScale + stagePos.x,
      y: cursor.cursor.y * stageScale + stagePos.y,
    };
  }, [cursor.cursor.x, cursor.cursor.y, stageScale, stagePos.x, stagePos.y]);

  // Check if cursor is within viewport (with some margin)
  const margin = 50;
  const isVisible =
    screenPos.x >= -margin &&
    screenPos.x <= viewportWidth + margin &&
    screenPos.y >= -margin &&
    screenPos.y <= viewportHeight + margin;

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Truncate long names
  const displayName = cursor.userName.length > 12
    ? cursor.userName.slice(0, 12) + '...'
    : cursor.userName;

  return (
    <div
      className="user-cursor"
      style={{
        position: 'absolute',
        left: screenPos.x,
        top: screenPos.y,
        pointerEvents: 'none',
        zIndex: 999999,
        transition: 'left 0.05s linear, top 0.05s linear',
        willChange: 'left, top',
      }}
    >
      {/* Cursor arrow SVG */}
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
        }}
      >
        {/* Cursor shape */}
        <path
          d="M4 4L12 20L14 14L20 12L4 4Z"
          fill={cursor.color}
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>

      {/* User name label */}
      <div
        className="user-cursor-label"
        style={{
          position: 'absolute',
          left: '16px',
          top: '16px',
          backgroundColor: cursor.color,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {displayName}
      </div>
    </div>
  );
};

/**
 * Container component for rendering multiple user cursors
 */
export const UserCursors: React.FC<UserCursorsProps> = ({
  cursors,
  stagePos,
  stageScale,
  viewportWidth,
  viewportHeight,
}) => {
  if (cursors.length === 0) {
    return null;
  }

  return (
    <div
      className="user-cursors-container"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 999999,
      }}
    >
      {cursors.map((cursor) => (
        <UserCursor
          key={cursor.userId}
          cursor={cursor}
          stagePos={stagePos}
          stageScale={stageScale}
          viewportWidth={viewportWidth}
          viewportHeight={viewportHeight}
        />
      ))}
    </div>
  );
};

export default UserCursor;
